import {Linking, Platform} from 'react-native';
import {toJSON} from 'flatted';

import config from '../config';
import {StoreType} from '../navigation/routes/types';
import {getStoreType} from '../utils/store';

import {BrandsStore} from './brandsStore';
import {CartStore} from './cartStore';
import {FeedStore} from './feedStore';
import {FilterStore} from './filterStore';
import {GlobalStore} from './globalStore';
import {LinesParams, LinesStore} from './linesStore';
import {LoyaltyInformation} from './loyaltyInformationStore';
import {OffersStore} from './offersStore';
import {PaymentStore} from './paymentStore';
import {PromotionsStore} from './promotionsStore';
import {RangeStore} from './rangeStore';
import {RewardsStore} from './rewardsStore';
import {SettingsStore} from './settingsStore';
import {ShopSelectionStore} from './shopSelectionStore';
import {UserStore} from './userStore';
import {WishlistStore} from './wishlistStore';

const getInitialURL = async () => {
  const initialURL = await Linking.getInitialURL();
  return initialURL ? new URL(initialURL) : null;
};

const getUrlSegments = (url: URL) =>
  url.pathname.split('/').filter(segment => segment !== '');

const isFirstUrlSegment = (urlStr: URL, segment = '') =>
  getUrlSegments(urlStr)[0] === segment;

const isRootURL = async () => {
  const url = await getInitialURL();
  if (!url && Platform.OS !== 'web') return true;

  return url && url.pathname === '/';
};

export const isAccountURL = async () => {
  const url = await getInitialURL();
  if (!url && Platform.OS !== 'web') return false;

  return url && url.pathname === '/password';
};

export const isInformationUrl = async () => {
  const url = await getInitialURL();
  if (!url && Platform.OS !== 'web') return false;
  return url && isFirstUrlSegment(url, 'information');
};

const isGiftCardUrl = async () => {
  const url = await getInitialURL();
  return url && isFirstUrlSegment(url, 'gift-cards');
};

const isStoreSwitchUrl = async () => {
  const url = await getInitialURL();
  if (!url) return false;

  const pathSegments = getUrlSegments(url);
  return pathSegments.length === 2 && pathSegments[0] === 'store';
};

const getStoreSwitchUrlSalesforceId = async () => {
  const url = await getInitialURL();
  if (!url) return null;

  const pathSegments = getUrlSegments(url);
  return pathSegments[1];
};

const GIFT_CARD_STORE = config.GIFT_CARD_STORE;

export class RootStore {
  brandsStore: BrandsStore;
  cartStore: CartStore;
  feedStore: FeedStore;
  filterStore: FilterStore;
  globalStore: GlobalStore;
  paymentStore: PaymentStore;
  promotionsStore: PromotionsStore;
  offersStore: OffersStore;
  rangeStore: RangeStore;
  shopSelectionStore: ShopSelectionStore;
  userStore: UserStore;
  wishlistStore: WishlistStore;
  rewardsStore: RewardsStore;
  loyaltyInformationStore: LoyaltyInformation;
  linesStore: Record<string, LinesStore> = {};
  settingsStore: SettingsStore;

  constructor() {
    this.brandsStore = new BrandsStore(this);
    this.cartStore = new CartStore(this);
    this.feedStore = new FeedStore(this);
    this.filterStore = new FilterStore(this);
    this.globalStore = new GlobalStore(this);
    this.paymentStore = new PaymentStore(this);
    this.promotionsStore = new PromotionsStore(this);
    this.offersStore = new OffersStore(this);
    this.rangeStore = new RangeStore(this);
    this.shopSelectionStore = new ShopSelectionStore(this);
    this.userStore = new UserStore(this);
    this.wishlistStore = new WishlistStore(this);
    this.rewardsStore = new RewardsStore(this);
    this.loyaltyInformationStore = new LoyaltyInformation(this);
    this.settingsStore = new SettingsStore(this);
  }

  resetOffersStore = () => {
    this.offersStore = new OffersStore(this);
  };

  public getLinesStore = (params: LinesParams) => {
    const key = toJSON(params);

    if (!this.linesStore[key]) {
      this.linesStore[key] = new LinesStore(params);
    }

    return this.linesStore[key];
  };

  private getShopSalesforceId = async () => {
    const isGiftCardStoreURL = await isGiftCardUrl();

    let localStorageStore =
      await this.shopSelectionStore.loadShopSalesforceIdFromLocalStorage();

    // Fix for store switch when there's no store selected
    const isStoreSwitch = await isStoreSwitchUrl();
    if (!localStorageStore && isStoreSwitch) {
      const storeSwitchSalesforceId = await getStoreSwitchUrlSalesforceId();
      if (storeSwitchSalesforceId) {
        await this.shopSelectionStore.setShopSalesforceIdToStorage(
          storeSwitchSalesforceId,
        );
      }
      localStorageStore = storeSwitchSalesforceId;
    }

    // TODO: A lot of the existing mobX code depends on the "current selected
    // store" loaded from the local storage. This tries to reuse that logic
    // without having to refactor all that right now. For this, we play with
    // a new  `LIQUOR_SHOP_SALESFORCE_STORAGE_KEY` that keeps the "previous
    // selected/preferred liquor store"
    let shopSalesforceId = null;
    if (isGiftCardStoreURL) {
      // If the user is visiting the Gift Card Store (/gift-cards), use the
      // fixed store "Salesforce ID" (number/id) for the Gift Card Store
      // (and also save the "preferred liquor store" to local storage if the
      // selected store was a liquor store)
      if (localStorageStore !== GIFT_CARD_STORE) {
        this.shopSelectionStore.setLiquorShopSalesforceIdToStorage(
          localStorageStore,
        );
      }
      shopSalesforceId = GIFT_CARD_STORE;
      this.shopSelectionStore.setShopSalesforceIdToStorage(shopSalesforceId);
    } else {
      // If the user is visiting the regular liquor store and the selected
      // store in local storage was the "Gift Card" store, check if there's a
      // selected ("preferred") liquor store in local storage and use that
      if (localStorageStore === GIFT_CARD_STORE) {
        let localStorageLiquorStore =
          await this.shopSelectionStore.loadLiquorShopSalesforceIdFromLocalStorage();

        // TODO: hack for old buggy dev versions
        if (localStorageLiquorStore === config.GIFT_CARD_STORE) {
          localStorageLiquorStore = null;
          await this.shopSelectionStore.clearLiquorShopSalesforceIdFromStorage();
        }

        if (localStorageLiquorStore) {
          await this.shopSelectionStore.setShopSalesforceIdToStorage(
            localStorageLiquorStore,
          );
        }
        shopSalesforceId = localStorageLiquorStore;
      } else {
        shopSalesforceId = localStorageStore;
      }
    }
    return shopSalesforceId;
  };

  // Note: This is a refactoring of a lot of mobX autorun code that was
  // distributed across the stores and was  hard to follow and maintain.
  // The referenced methods are still unchanged.
  // (Use reactions sparingly!
  // https://mobx.js.org/reactions.html#use-reactions-sparingly)
  initialiseApp = async () => {
    this.globalStore.setIsAppInitialising(true);
    try {
      // 1. Load the device ID from local storage or generate a new one
      //    Sets `this.globalStore?.deviceId`
      await this.globalStore.loadDeviceIdFromLocalStoreOrGenerateNew();

      // 2. Load the previously selected store "Salesforce ID" from local storage
      //    or check if the user is visiting the "Gift Cards Store" (/gift-cards)

      let shopSalesforceId = null;

      /**
       * Only for web
       */
      const storeId =
        Platform.OS === 'web' &&
        new URLSearchParams(window.location.search).get('storeId');

      if (storeId) {
        shopSalesforceId = storeId;
        // await this.shopSelectionStore.setShopSalesforceIdToStorage(storeId);
        await this.shopSelectionStore.findSalesforceShop(storeId);
      } else {
        shopSalesforceId = await this.getShopSalesforceId();
      }

      const isAccountRoute = await isAccountURL();
      if (isAccountRoute) {
        this.globalStore.setIsAccountRoute(true);
      }

      const isInformationRoute = await isInformationUrl();
      if (isInformationRoute) {
        this.globalStore.setIsInformationRoute(true);
      }

      if (!shopSalesforceId) {
        // 2.1. If a store was not selected, finish the loading process (the
        //      "Authentication flow" in App.tsx will show the store selection flow)
        this.globalStore.setIsAppInitialising(false);
        this.globalStore.setWasStoreSelectedOnAppStart(false);
        this.loadDefaultStoreContent();
        return;
      }

      // 3. Load the session id and session secret from local storage. If they
      //    exist, refresh the session. Sets:
      //    - rootStore.userStore.sessionSecret
      //    - rootStore.userStore.sessionToken
      //    - rootStore.userStore.sessionId
      await this.userStore.retrieveSessionInformation(); // logout? this can regenerate the device id

      // 4. Load the store data (feed, promotions, brands, segments, etc)
      await this.shopSelectionStore.retrieveShopData();
      const isRoot = await isRootURL();
      if (isRoot) {
        // This is the "traditional" app start. There are too many initial requests and waiting
        // for all of them takes too long. More refactoring is still needed.
        this.initialiseSelectedStoreData();
      } else {
        // A lot of the "deep links" require the store data to be loaded. We are currently willing
        // to pay the price of waiting in these cases
        await this.initialiseSelectedStoreData();
      }

      if (this.userStore.isLoggedIn()) {
        await this.initialiseUserData();
      }

      this.globalStore.setIsAppInitialising(false);
    } catch (err) {
      console.error('Error initialising app', err);
      this.globalStore.setIsAppInitialising(false);
    }
  };

  loadDefaultStoreContent = () => {
    this.feedStore.loadFeedList(
      config.DEFAULT_STORE,
      config.DEFAULT_STORE_STATE,
    );
    this.feedStore.getSegments(
      config.DEFAULT_STORE,
      config.DEFAULT_STORE_STATE,
    );
    this.promotionsStore.getPromotions(
      config.DEFAULT_STORE,
      config.DEFAULT_STORE_STATE,
    );
  };

  initialiseUserSession = async () => {
    // Sets `this.globalStore?.deviceId`
    await this.globalStore.loadDeviceIdFromLocalStoreOrGenerateNew();

    // sets
    // rootStore.userStore.sessionSecret
    // rootStore.userStore.sessionToken
    // rootStore.userStore.sessionId
    await this.userStore.retrieveSessionInformation(); // logout? this can regenerate the device id
  };

  initialiseUserDataNonBlocking = async () => {
    this.loyaltyInformationStore.reset();
    await this.userStore.retrieveUserInfoV2({retryable: true});
    this.cartStore.fullyReloadOrderFromMFL();
    this.paymentStore.loadTillPaymentInstrumentForUser();
    this.rewardsStore.loadRewardsForUser(this.userStore.phoneNumber);

    // A store should be selected already but just in case...
    if (this.shopSelectionStore?.selectedStore) {
      await this.paymentStore.loadPaymentServicesForStore();
      this.wishlistStore.loadWishlistForUser();
    }
  };

  login = async () => {
    await this.initialiseUserData();
    await this.initialiseSelectedStoreLoyaltyDependentData();
  }

  initialiseUserData = async () => {
    this.globalStore.setIsUserDataInitialising(true);
    try {
      await this.initialiseUserDataNonBlocking();
      this.globalStore.setIsUserDataInitialising(false);
    } catch (err) {
      this.globalStore.setIsUserDataInitialising(false);
      console.error('Error initialising user data', err);
    }
  };

  initialiseSelectedStoreData = async () => {
    try {
      const selectedStore = this.shopSelectionStore?.selectedStore;
      if (!selectedStore?.id) throw new Error('No store selected');

      await this.feedStore.loadFeedList(); // needs shop id
      await this.initialiseSelectedStoreLoyaltyDependentData();
      await this.brandsStore.loadBrands(); // no dependencies
      await this.feedStore.getSegments(); // needs shop id
      await this.rangeStore.loadDepartments();
      this.filterStore.loadSortOptions();
      this.filterStore.loadFilterOptions();
      this.shopSelectionStore.loadLiquorLicense();
      this.cartStore.fullyReloadOrderFromMFL();

      if (this.userStore.isLoggedIn()) {
        this.wishlistStore.loadWishlistForUser();
        this.paymentStore.loadPaymentServicesForStore();
      }
      if (getStoreType(selectedStore) === StoreType.GiftCardStore) {
        await this.rangeStore.loadRangeLines();
      }
    } catch (error) {
      console.error('Error initialising app', error);
      this.globalStore.setIsSwitchingStore(false);
    }
  };

  initialiseSelectedStoreLoyaltyDependentData = async () => {
    await this.promotionsStore.getPromotions(
      this.shopSelectionStore.selectedStore?.store_number,
      this.shopSelectionStore.selectedStore?.address?.administrative_area,
    );
  };

  logoutUser = async () => {
    try {
      await this.cartStore.fullyReloadOrderFromMFL();
      await this.initialiseSelectedStoreLoyaltyDependentData();
      this.wishlistStore.reset();
      await this.rewardsStore.logout();
      this.loyaltyInformationStore.reset();
    } catch (err) {
      console.error('Error logging out user', err);
    }
  };
}

export const rootStore = new RootStore();
